/**
 * API Service Layer
 *
 * Centralized API client using axios for all backend communication.
 */

import axios from 'axios';
import { API_URL } from '../config/constants';

/**
 * Create axios instance with default configuration
 */
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to add auth token
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('okta-token-storage');

    if (token) {
      try {
        const tokenData = JSON.parse(token);
        const accessToken = tokenData?.accessToken?.accessToken;

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      if (status === 401) {
        // Unauthorized - redirect to login
        localStorage.removeItem('okta-token-storage');
        window.location.href = '/login';
      }

      // Return formatted error
      return Promise.reject({
        status,
        message: data.message || 'An error occurred',
        errors: data.errors || [],
      });
    } else if (error.request) {
      // Request made but no response
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your connection.',
      });
    } else {
      // Something else happened
      return Promise.reject({
        status: 0,
        message: error.message || 'An unexpected error occurred',
      });
    }
  }
);

/**
 * API Service Methods
 */
const api = {
  // Auth
  getCurrentUser: () => apiClient.get('/api/auth/user'),
  updateUserProfile: (data) => apiClient.put('/api/auth/user', data),
  logout: () => apiClient.post('/api/auth/logout'),
  getAllUsers: () => apiClient.get('/api/auth/users'),

  // Requests
  getRequests: (params) => apiClient.get('/api/requests', { params }),
  getRequestById: (id) => apiClient.get(`/api/requests/${id}`),
  createRequest: (data) => apiClient.post('/api/requests', data),
  updateRequest: (id, data) => apiClient.put(`/api/requests/${id}`, data),
  autoSaveRequest: (id, answers) => apiClient.post(`/api/requests/${id}/autosave`, { answers }),
  submitRequest: (id) => apiClient.post(`/api/requests/${id}/submit`),
  deleteRequest: (id) => apiClient.delete(`/api/requests/${id}`),
  getRequestFieldChanges: (id) => apiClient.get(`/api/requests/${id}/field-changes`),
  checkDuplicate: (params) => apiClient.get('/api/requests/check-duplicate', { params }),
  checkBCForCuC: (params) => apiClient.get('/api/requests/check-bc-for-cuc', { params }),
  checkBCForCiC: (params) => apiClient.get('/api/requests/check-bc-for-cic', { params }),
  checkBCInProgressForCiC: (params) => apiClient.get('/api/requests/check-bc-in-progress-for-cic', { params }),
  checkGAPrefill: (params) => apiClient.get('/api/requests/check-ga-prefill', { params }),
  checkEAPrefill: (params) => apiClient.get('/api/requests/check-ea-prefill', { params }),
  checkPPPExists: (params) => apiClient.get('/api/requests/check-ppp-exists', { params }),
  getMyProductRequests: () => apiClient.get('/api/requests/my-product-requests'),
  getMyDashboardRequests: () => apiClient.get('/api/requests/my-dashboard-requests'),
  updateApproverTypes: (userId, approverTypes) => apiClient.put(`/api/auth/users/${userId}/approver-types`, { approverTypes }),
  updateUserRole: (userId, role) => apiClient.put(`/api/auth/users/${userId}/role`, { role }),
  devSetMyRole: (role) => apiClient.post('/api/dev/set-my-role', { role }),
  sendTestEmail: (to, name) => apiClient.post('/api/email/test', { to, name }),
  importUsers: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/auth/users/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/requests/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Products
  getProducts: (params) => apiClient.get('/api/products', { params }),
  getProductById: (id) => apiClient.get(`/api/products/${id}`),
  updateProduct: (id, data) => apiClient.put(`/api/products/${id}`, data),
  createProduct: (data) => apiClient.post('/api/products', data),
  deleteProduct: (id) => apiClient.delete(`/api/products/${id}`),
  setProductStatus: (id, status) => apiClient.patch(`/api/products/${id}/status`, { status }),
  getProductHistory: (id) => apiClient.get(`/api/products/${id}/history`),
  checkProductName: (name) => apiClient.get('/api/products/check-name', { params: { name } }),
  checkProductCode: (code) => apiClient.get('/api/products/check-code', { params: { code } }),
  exportProducts: (skiRelease) => apiClient.get('/api/products/export', { params: { skiRelease }, responseType: 'blob' }),
  seedProducts: (file) => {
    const formData = new FormData();
    formData.append('seedFile', file);
    return apiClient.post('/api/products/seed', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // Ski Releases
  getSkiReleases: (params) => apiClient.get('/api/ski-releases', { params }),
  getCurrentTimeline: () => apiClient.get('/api/ski-releases/current/timeline'),

  // Questions
  getQuestions: (formType) => apiClient.get('/api/questions', { params: { form_type: formType } }),
  getFormTypes: () => apiClient.get('/api/questions/form-types'),

  // Reports
  getHierarchyReport: (params) => apiClient.get('/api/reports/hierarchy', { params }),
  getSummaryStats: (params) => apiClient.get('/api/reports/summary', { params }),
  getFilterOptions: () => apiClient.get('/api/reports/filter-options'),

  // Email
  sendEmail: (data) => apiClient.post('/api/email/send', data),
  getEmailHistory: (requestId) => apiClient.get(`/api/email/history/${requestId}`),

  // Export
  exportRequests: (params) => apiClient.get('/api/export/requests', { params }),
  getExportCount: (params) => apiClient.get('/api/export/count', { params }),
  exportProductCatalog: (params) => apiClient.get('/api/export/product-catalog', { params }),
  getProductCatalogCount: (params) => apiClient.get('/api/export/product-catalog-count', { params }),

  // Admin
  getMetrics: (params) => apiClient.get('/api/admin/metrics', { params }),
  getUsers: () => apiClient.get('/api/admin/users'),
  updateUserStatus: (id, isActive) => apiClient.put(`/api/admin/users/${id}/status`, { is_active: isActive }),
  getSeedDataStatus: () => apiClient.get('/api/admin/seed-data-status'),
  loadSeedData: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/admin/load-seed-data', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getSeedProductStatus: () => apiClient.get('/api/admin/product-seed-status'),
  loadProductSeedData: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/api/admin/load-product-seed-data', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getAdminStatistics: () => apiClient.get('/api/admin/statistics'),
  getPendingProductRequests: () => apiClient.get('/api/admin/new-product-requests'),
  createProductFromRequest: (requestId, productData) => apiClient.post(`/api/admin/new-product-requests/${requestId}/create-product`, productData),
  rejectProductRequest: (requestId, reason) => apiClient.post(`/api/admin/new-product-requests/${requestId}/reject`, { reason }),

  // Delegation
  getDelegationGroups: (requestId) => apiClient.get(`/api/requests/${requestId}/delegation/groups`),
  initiateDelegation: (requestId, data) => apiClient.post(`/api/requests/${requestId}/delegation`, data),
  getDelegationHistory: (requestId) => apiClient.get(`/api/requests/${requestId}/delegation`),
  submitDelegationAssignments: (requestId, data) => apiClient.post(`/api/requests/${requestId}/delegation/assignments/submit`, data),
  cancelDelegation: (requestId) => apiClient.delete(`/api/requests/${requestId}/delegation`),

  // Generic HTTP methods for flexibility
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  delete: (url, config) => apiClient.delete(url, config),
};

export default api;
